import { useCallback, useEffect, useRef, useState } from 'react';

// Composer voice input. Records one clip with MediaRecorder, hands it to
// `onTranscribe` (the backend turns it into text) and passes the text to
// `onText`. Nothing is sent to Atlas from here: the transcript lands in the
// composer for the user to review.

export const MAX_RECORDING_MS = 10 * 60 * 1000;
const MIN_RECORDING_MS = 700;

// Silence timeout, from the same level meter the waveform uses: after this long
// with no speech the recording stops by itself, and a clip with no speech at all
// is dropped instead of being sent (and paid for) as a transcription.
export const SILENCE_STOP_MS = 30 * 1000;
const LEVEL_SAMPLE_MS = 250;
const VOICE_LEVEL = 0.02; // RMS of the time-domain signal, 0..1
const SPEECH_LEVEL = 0.03;
const SPEECH_SAMPLES_TO_COUNT = 2; // ~0.5 s above SPEECH_LEVEL counts as speech

/** RMS level (0..1) of an analyser's current time-domain frame. */
export function frameLevel(analyserNode, buffer) {
  analyserNode.getByteTimeDomainData(buffer);
  let sum = 0;
  for (let i = 0; i < buffer.length; i += 1) {
    const centred = (buffer[i] - 128) / 128;
    sum += centred * centred;
  }
  return Math.sqrt(sum / buffer.length);
}

// Chrome/Edge/Firefox record webm/opus, Safari (incl. iOS) records mp4/aac.
const MIME_CANDIDATES = ['audio/webm;codecs=opus', 'audio/webm', 'audio/mp4', 'audio/ogg;codecs=opus'];

export function isVoiceInputSupported() {
  return typeof window !== 'undefined'
    && typeof window.MediaRecorder !== 'undefined'
    && Boolean(navigator?.mediaDevices?.getUserMedia);
}

export function pickRecorderMimeType(isTypeSupported = (type) => window.MediaRecorder.isTypeSupported(type)) {
  return MIME_CANDIDATES.find((type) => {
    try {
      return isTypeSupported(type);
    } catch {
      return false;
    }
  }) || '';
}

/** Add dictated text to whatever is already typed, with one space between. */
export function appendTranscript(existing, transcript) {
  const text = (transcript || '').trim();
  if (!text) return existing || '';
  if (!existing || !existing.trim()) return text;
  return /\s$/.test(existing) ? `${existing}${text}` : `${existing} ${text}`;
}

function microphoneErrorMessage(error) {
  switch (error?.name) {
    case 'NotAllowedError':
    case 'SecurityError':
      return 'Microphone access is blocked. Allow it in your browser’s site settings to talk to Atlas.';
    case 'NotFoundError':
    case 'OverconstrainedError':
      return 'No microphone was found.';
    case 'NotReadableError':
      return 'Your microphone is in use by another app.';
    default:
      return 'Could not start the microphone.';
  }
}

export default function useVoiceRecorder({ onTranscribe, onText, maxMs = MAX_RECORDING_MS, silenceMs = SILENCE_STOP_MS }) {
  // idle → starting (permission prompt) → recording → transcribing → idle
  const [status, setStatus] = useState('idle');
  const [error, setError] = useState(null);
  const [startedAt, setStartedAt] = useState(null);
  const [analyser, setAnalyser] = useState(null);

  const recorderRef = useRef(null);
  const streamRef = useRef(null);
  const audioContextRef = useRef(null);
  const chunksRef = useRef([]);
  const startedAtRef = useRef(0);
  const discardRef = useRef(false);
  const limitTimerRef = useRef(null);
  const levelTimerRef = useRef(null);
  const meterRef = useRef(null);
  const speechRef = useRef({ metered: false, speechSamples: 0, lastVoiceAt: 0 });
  const mountedRef = useRef(true);
  const callbacksRef = useRef({ onTranscribe, onText });
  callbacksRef.current = { onTranscribe, onText };

  const releaseDevices = useCallback(() => {
    clearTimeout(limitTimerRef.current);
    limitTimerRef.current = null;
    clearInterval(levelTimerRef.current);
    levelTimerRef.current = null;
    streamRef.current?.getTracks().forEach((track) => track.stop());
    streamRef.current = null;
    audioContextRef.current?.close().catch(() => {});
    audioContextRef.current = null;
    meterRef.current = null;
    if (mountedRef.current) setAnalyser(null);
  }, []);

  const handleStopped = useCallback(async (mimeType) => {
    const durationMs = Date.now() - startedAtRef.current;
    const chunks = chunksRef.current;
    chunksRef.current = [];
    recorderRef.current = null;
    releaseDevices();
    if (!mountedRef.current) return;
    setStartedAt(null);

    if (discardRef.current) {
      setStatus('idle');
      return;
    }
    const speech = speechRef.current;
    if (speech.metered && speech.speechSamples < SPEECH_SAMPLES_TO_COUNT) {
      setStatus('idle');
      setError('No speech was heard, so nothing was sent.');
      return;
    }
    const blob = new Blob(chunks, { type: (mimeType || 'audio/webm').split(';')[0] });
    if (durationMs < MIN_RECORDING_MS || blob.size === 0) {
      setStatus('idle');
      setError('That was too short to catch. Tap the mic and speak, then tap ✓.');
      return;
    }

    setStatus('transcribing');
    try {
      const text = await callbacksRef.current.onTranscribe(blob, { durationMs });
      if (!mountedRef.current) return;
      if (text && text.trim()) callbacksRef.current.onText(text.trim());
      else setError('No speech was detected in that recording.');
    } catch (err) {
      if (mountedRef.current) setError(err?.message || 'Could not transcribe that recording.');
    } finally {
      if (mountedRef.current) setStatus('idle');
    }
  }, [releaseDevices]);

  const start = useCallback(async () => {
    if (status !== 'idle' || !isVoiceInputSupported()) return;
    setError(null);
    setStatus('starting');
    let stream;
    try {
      stream = await navigator.mediaDevices.getUserMedia({
        audio: { echoCancellation: true, noiseSuppression: true, autoGainControl: true },
      });
    } catch (err) {
      if (mountedRef.current) {
        setStatus('idle');
        setError(microphoneErrorMessage(err));
      }
      return;
    }
    if (!mountedRef.current) {
      stream.getTracks().forEach((track) => track.stop());
      return;
    }

    const mimeType = pickRecorderMimeType();
    let recorder;
    try {
      // 32 kbps opus is plenty for speech and keeps 10 minutes near 2.5 MB.
      recorder = new window.MediaRecorder(stream, mimeType ? { mimeType, audioBitsPerSecond: 32000 } : undefined);
    } catch {
      stream.getTracks().forEach((track) => track.stop());
      setStatus('idle');
      setError('This browser cannot record audio.');
      return;
    }

    streamRef.current = stream;
    recorderRef.current = recorder;
    chunksRef.current = [];
    discardRef.current = false;
    recorder.ondataavailable = (event) => {
      if (event.data && event.data.size > 0) chunksRef.current.push(event.data);
    };
    recorder.onstop = () => handleStopped(recorder.mimeType || mimeType);

    // Level meter for the waveform and the silence timeout; recording works without it.
    speechRef.current = { metered: false, speechSamples: 0, lastVoiceAt: 0 };
    try {
      const AudioContextClass = window.AudioContext || window.webkitAudioContext;
      if (AudioContextClass) {
        const context = new AudioContextClass();
        const node = context.createAnalyser();
        node.fftSize = 512;
        context.createMediaStreamSource(stream).connect(node);
        context.resume?.().catch(() => {});
        audioContextRef.current = context;
        meterRef.current = node;
        setAnalyser(node);
      }
    } catch {
      setAnalyser(null);
    }

    recorder.start(1000);
    startedAtRef.current = Date.now();
    setStartedAt(startedAtRef.current);
    setStatus('recording');
    limitTimerRef.current = setTimeout(() => {
      if (recorderRef.current?.state === 'recording') recorderRef.current.stop();
    }, maxMs);

    const meter = meterRef.current;
    if (meter && silenceMs > 0) {
      const buffer = new Uint8Array(meter.fftSize);
      speechRef.current = { metered: true, speechSamples: 0, lastVoiceAt: Date.now() };
      levelTimerRef.current = setInterval(() => {
        if (recorderRef.current?.state !== 'recording') return;
        let level = 0;
        try {
          level = frameLevel(meter, buffer);
        } catch {
          return;
        }
        const now = Date.now();
        const speech = speechRef.current;
        if (level >= VOICE_LEVEL) speech.lastVoiceAt = now;
        if (level >= SPEECH_LEVEL) speech.speechSamples += 1;
        if (now - speech.lastVoiceAt >= silenceMs) recorderRef.current.stop();
      }, LEVEL_SAMPLE_MS);
    }
  }, [handleStopped, maxMs, silenceMs, status]);

  const finish = useCallback(() => {
    discardRef.current = false;
    if (recorderRef.current?.state === 'recording') recorderRef.current.stop();
  }, []);

  const cancel = useCallback(() => {
    discardRef.current = true;
    if (recorderRef.current?.state === 'recording') recorderRef.current.stop();
    else releaseDevices();
  }, [releaseDevices]);

  const clearError = useCallback(() => setError(null), []);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      discardRef.current = true;
      if (recorderRef.current?.state === 'recording') recorderRef.current.stop();
      releaseDevices();
    };
  }, [releaseDevices]);

  return {
    supported: isVoiceInputSupported(),
    status,
    error,
    startedAt,
    analyser,
    maxMs,
    start,
    finish,
    cancel,
    clearError,
  };
}
